import { useCallback, useEffect, useRef, useState } from 'react';

import { AnalyticsEvent, trackClick } from '@bonusly/analytics';
import { Box, useColorModeValue } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { Emoji } from '../../../../../components/check-ins/emoji';
import { Placeholder } from './placeholder';
import { UserSelection } from './user-selection';

const EMOJI_WIDTH = 40; // hardcoded because scale of emoji will change its actual size
const EMOJI_PADDING = 10;

interface ScaleProps {
	editable?: boolean;
	selfSelection?: number | null;
	participantId?: string;
	participantSelection?: number | null;
	participantDimmed?: boolean;
	onSelectionChange?: (value: number) => void;
}

export function Scale({ editable = true, selfSelection, participantId, participantSelection, participantDimmed, onSelectionChange }: ScaleProps) {
	// value is a 1.0 to 5.0 value representing the position of the mouse on the scale
	const trackRef = useRef<HTMLDivElement>(null);
	const emojiRef = useRef<HTMLDivElement>(null);
	const [value, setValue] = useState<number>(3);
	const [active, setActive] = useState<boolean>(false);
	const [trackWidth, setTrackWidth] = useState<number>(80);
	const currentUser = useCurrentUser();
	const showPlaceholder = !active && !selfSelection && !participantSelection;

	// Gradient for the mood scale track - light pastels for light mode, darker tones for dark mode
	const scaleGradient = useColorModeValue(
		'linear-gradient(90deg, #D7DFFA 0%, #EBF2F4 50.5%, #D5F7D5 100%)',
		'linear-gradient(90deg, #3B4A7A 0%, #4A5568 50.5%, #3A6B3A 100%)'
	);

	selfSelection = selfSelection || null;
	participantSelection = participantSelection || null;
	participantDimmed = participantDimmed || false;

	// Set up resize observer to track changes in the track element's size (this changes with the window/drawer size)
	useEffect(() => {
		if (!trackRef.current) return;

		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				if (entry.target === trackRef.current) {
					setTrackWidth(entry.contentRect.width);
				}
			}
		});

		resizeObserver.observe(trackRef.current);

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	const getMeasures = useCallback(
		function ({ clientX, cappedValue }: { clientX?: number; cappedValue?: number | null }) {
			let trackPos = 0;
			let emojiPos = 0;
			let percentValue = 0;
			let scaleValue = 0;

			const bounds = trackRef.current?.getBoundingClientRect();
			const trackX = bounds?.left || 0;
			// Use the state value instead of getting it from bounds
			const currentTrackWidth = trackWidth;

			const emojiWidth = EMOJI_WIDTH;
			const emojiPadding = EMOJI_PADDING;
			const emojiAreaWidth = currentTrackWidth - emojiWidth - emojiPadding;

			// default is right in the middle
			if (!cappedValue && !clientX) {
				cappedValue = value;
			}

			// use input or default to calculate the measures on the track
			if (cappedValue) {
				percentValue = (cappedValue - 1) / 4;
				// trackPos = (percentValue * emojiAreaWidth);
				// clientX = Math.floor(trackPos + trackX);
			} else if (clientX) {
				trackPos = Math.max(0, Math.min(Number(clientX - trackX), currentTrackWidth)) || 0;
				emojiPos = Math.max(0, trackPos - emojiWidth / 2);
				emojiPos = Math.min(emojiPos, emojiAreaWidth);
				percentValue = emojiPos / emojiAreaWidth;
			}

			// calculate the measure on the selectable emoji spectrum
			//  track-area : |------------------------|
			//  emoji-area :  |----------------------|
			//  emoji      : (-)

			// then normalize our messurements by the emoji areas, and the single decimal point values
			scaleValue = Math.round(percentValue * 40) / 10 + 1;
			cappedValue = Math.max(1, Math.min(5, scaleValue));

			percentValue = (cappedValue - 1) / 4;
			trackPos = Math.round(percentValue * currentTrackWidth);

			const emojiPercent = percentValue * emojiAreaWidth;
			emojiPos = Math.round(emojiPercent + emojiPadding / 2);

			return {
				trackPos,
				trackWidth: currentTrackWidth,
				emojiPos,
				emojiWidth,
				emojiAreaWidth,
				emojiPadding,
				cappedValue,
				percentValue,
				left: emojiPos,
				leftPx: `${emojiPos}px`,
			};
		},
		[value, trackWidth]
	);

	const onMouseMove = useCallback(
		(e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
			const pos = 'clientX' in e ? e.clientX : e.touches[0].clientX;
			const mouseMeasures = getMeasures({ clientX: pos });
			const emojiMeasures = getMeasures({ cappedValue: value });

			// Check if hovering near edges (within 20% of track width from either edge)
			const isNearEdge = mouseMeasures.left <= trackWidth * 0.2 || mouseMeasures.left >= trackWidth * 0.8;

			// Allow immediate activation when near edges or when far enough from current emoji position
			if (isNearEdge || Math.abs(mouseMeasures.left - emojiMeasures.left) > EMOJI_WIDTH / 2) {
				setActive(true);
			}

			// update the emoji as the cursor moves if active
			if (active && mouseMeasures.cappedValue !== value) {
				setValue(mouseMeasures.cappedValue);
			}
		},
		[value, active, setValue, setActive, getMeasures]
	);

	const onTouchStart = useCallback(
		(e: React.TouchEvent<HTMLDivElement>) => {
			onMouseMove(e);
		},
		[onMouseMove]
	);

	const onTouchEnd = useCallback(() => {
		setActive(false);
	}, []);

	const onSelect = useCallback(
		(e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
			e.preventDefault();
			onSelectionChange?.(value);
			// delay before removing emoji to let the user see the selection
			setTimeout(() => setActive(false), 300);
		},
		[value, setActive, onSelectionChange]
	);

	const selfMeasure = !selfSelection ? null : getMeasures({ cappedValue: selfSelection });
	const participantMeasure = !participantSelection ? null : getMeasures({ cappedValue: participantSelection });

	// we'll use this below to determine if the self and participant need to be slightly offset from eachother.
	let overlappingScores = false;
	let participantOffset = participantSelection || 0;
	let participantScoreLeft = false;
	let selfScoreLeft = false;
	let selfPosition: string | number = selfMeasure?.left || 0;
	let participantPosition: string | number = participantMeasure?.left || 0;
	if (selfSelection && participantSelection) {
		if (selfMeasure && participantMeasure) {
			const scoreDelta = Math.abs(selfMeasure.left - participantMeasure.left);

			selfPosition = selfMeasure.left;
			participantPosition = participantMeasure.left;

			overlappingScores = overlappingScores || scoreDelta < 35;
			if (overlappingScores) {
				participantScoreLeft = scoreDelta ? selfSelection > participantSelection : selfSelection > 3;
				selfScoreLeft = !participantScoreLeft;
				participantOffset = (participantScoreLeft ? -1 : 1) * (25 - scoreDelta);
				// litteraly~ to prevent an edge case
				participantPosition = Math.max(
					participantMeasure.emojiPadding / 2,
					Math.min(participantPosition + participantOffset, participantMeasure.emojiAreaWidth)
				);
			}
		}
	}
	if (typeof selfPosition === 'number') selfPosition = `calc(${selfPosition}px)`;
	if (typeof participantPosition === 'number') participantPosition = `calc(${participantPosition}px)`;

	return (
		<Box
			pointerEvents={editable ? 'auto' : 'none'}
			data-component="scale"
			ref={trackRef}
			bg={scaleGradient}
			cursor="pointer"
			h={12}
			minW={80}
			onMouseLeave={() => setTimeout(() => onTouchEnd(), 200)}
			onMouseMove={onMouseMove}
			onTouchStart={onTouchStart}
			onTouchEnd={onSelect}
			onTouchMove={onMouseMove}
			onClick={onSelect}
			rounded="md"
			style={{ touchAction: 'none' }}
			pos="relative"
			userSelect="none"
			{...(editable && trackClick(AnalyticsEvent.CheckIns.MeetingCheckInScoreSelected))}
		>
			<Placeholder opacity={showPlaceholder ? 1 : 0} pointerEvents="none" transition="opacity 0.4s" />

			<UserSelection
				userId={currentUser.id}
				value={selfSelection}
				leftNumbers={selfScoreLeft}
				hideNumbers={active}
				borderRadius="full"
				boxShadow={active ? '0 2px 1px 0 rgba(0, 0, 0, 0.12)' : ''}
				left={selfPosition}
				opacity={selfSelection ? 1 : 0}
				pointerEvents="none"
				pos="absolute"
				top="calc(50% - 20px)"
				transformOrigin="center"
				transition="left 50ms, opacity 300ms, transform 300ms"
				zIndex={2}
			/>

			<UserSelection
				userId={participantId}
				value={participantSelection}
				leftNumbers={participantScoreLeft}
				hideNumbers={active}
				left={participantPosition}
				opacity={participantSelection ? (participantDimmed || active ? 0.5 : 1) : 0}
				pointerEvents="none"
				pos="absolute"
				top="calc(50% - 20px)"
				transition="left 50ms, opacity 0.25s"
				zIndex={1}
			/>

			<Emoji
				ref={emojiRef}
				value={value}
				borderRadius="full"
				boxShadow={active ? 'md' : 'none'}
				left={getMeasures({ cappedValue: value }).left + 'px'}
				opacity={active ? 1 : 0}
				pointerEvents="none"
				pos="absolute"
				top="calc(50% - 20px)"
				transform={active ? 'scale(1.5)' : 'scale(1)'}
				transformOrigin="center"
				transition="left 50ms, opacity 300ms, transform 300ms"
				zIndex={3}
			/>
		</Box>
	);
}
